import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useEffect, type RefObject } from 'react';
import {
  useForm,
  useFormContext,
  useWatch,
  type UseFormReturn,
} from 'react-hook-form';
import {
  validateEmail as validateEmailUntyped,
  validatePhone as validatePhoneUntyped,
} from '~/commonComponents/CommonFunctions';
import { useSendRecoveryCode } from '~/domain/ForgotPassword';
import { formatPhoneOnChange } from '~/utils/formatPhoneOnChange';
import { useAuthFormSubmit } from '../../shared';

const validateEmail = validateEmailUntyped as (value: string) => boolean;
const validatePhone = validatePhoneUntyped as (value: string) => boolean;

export const EMPTY_FIELD_SENTINEL = 'empty';
export const INVALID_EMAIL_MESSAGE = 'Please enter a valid email.';
export const INVALID_PHONE_MESSAGE = 'Please enter a valid phone number.';

export type TForgotPasswordMethod = 'email' | 'phone';

export interface IChooseMethodStepValues {
  method: TForgotPasswordMethod;
  email: string;
  phone: string;
}

export interface IChooseMethodFormCallbacks {
  onCodeSent: (args: { identifier: string; method: TForgotPasswordMethod }) => void;
}

export interface IChooseMethodFormController {
  methods: UseFormReturn<IChooseMethodStepValues>;
}

export const useChooseMethodForm = (): IChooseMethodFormController => {
  const methods = useForm<IChooseMethodStepValues>({
    defaultValues: { method: 'email', email: '', phone: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  return { methods };
};

export interface IUseChooseMethodFormLogicResult {
  submit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  bannerVisible: boolean;
  bannerRef: RefObject<HTMLDivElement | null>;
  dismissBanner: () => void;
  isPending: boolean;
  watchedMethod: TForgotPasswordMethod;
  bannerMessage: string | undefined;
  showBanner: boolean;
  emailRules: { validate: (value: string) => string | boolean };
  phoneRules: { validate: (value: string) => string | boolean };
  handleSelectMethod: (method: TForgotPasswordMethod) => void;
  handleInputChange: (field: 'email' | 'phone', value: string) => void;
  emailHasError: boolean;
  phoneHasError: boolean;
}

export const useChooseMethodFormLogic = ({
  onCodeSent,
}: IChooseMethodFormCallbacks): IUseChooseMethodFormLogicResult => {
  const {
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useFormContext<IChooseMethodStepValues>();

  const { mutate, isPending, isError, error, isSuccess, reset, variables } =
    useSendRecoveryCode();

  const { submit, bannerVisible, bannerRef, dismissBanner } =
    useAuthFormSubmit<IChooseMethodStepValues>({
      onValid: (values) => {
        if (values.method === 'email') {
          mutate({ identifier: values.email.trim().toLowerCase() });
          return;
        }
        const parsed = parsePhoneNumberFromString(values.phone, 'US');
        if (!parsed?.number) return;
        mutate({ identifier: parsed.number });
      },
    });

  const emailRules = {
    validate: (value: string) => {
      const method = getValues('method');
      const phone = getValues('phone');
      if (method === 'email') {
        if (!value) return EMPTY_FIELD_SENTINEL;
        return validateEmail(value) || INVALID_EMAIL_MESSAGE;
      }
      return value || phone ? true : EMPTY_FIELD_SENTINEL;
    },
  };
  const phoneRules = {
    validate: (value: string) => {
      const method = getValues('method');
      const email = getValues('email');
      if (method === 'phone') {
        if (!value) return EMPTY_FIELD_SENTINEL;
        return validatePhone(value) || INVALID_PHONE_MESSAGE;
      }
      return value || email ? true : EMPTY_FIELD_SENTINEL;
    },
  };

  const watchedEmail = useWatch({ control, name: 'email' });
  const watchedPhone = useWatch({ control, name: 'phone' });
  const watchedMethod = useWatch({ control, name: 'method' });

  useEffect(() => {
    if (isError) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedEmail, watchedPhone]);

  useEffect(() => {
    if (!isSuccess || !variables) return;
    onCodeSent({ identifier: variables.identifier, method: watchedMethod });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const beErrorMessage = isError
    ? error instanceof Error
      ? error.message
      : 'Something went wrong. Please try again.'
    : undefined;

  const rawValidationMessage = errors.email?.message ?? errors.phone?.message ?? undefined;
  const validationMessage =
    rawValidationMessage === EMPTY_FIELD_SENTINEL ? undefined : rawValidationMessage;

  const showBanner = bannerVisible || isError;
  const bannerMessage = beErrorMessage ?? validationMessage;

  const handleSelectMethod = (method: TForgotPasswordMethod) => {
    setValue('method', method);
  };

  const handleInputChange = (field: 'email' | 'phone', value: string) => {
    // Mask phone digits as the user types: `5551234567` → `(555) 123-4567`.
    // formatPhoneOnChange wraps AsYouType so backspacing past a generated
    // format char (e.g. ")" in "(555)") doesn't get stuck.
    const next = field === 'phone' ? formatPhoneOnChange(getValues('phone'), value) : value;
    setValue(field, next);
    // Typing in either input clears both red borders + the banner. Re-validation
    // runs on the next submit.
    clearErrors(['email', 'phone']);
    dismissBanner();
  };

  return {
    submit,
    bannerVisible,
    bannerRef,
    dismissBanner,
    isPending,
    watchedMethod,
    bannerMessage,
    showBanner,
    emailRules,
    phoneRules,
    handleSelectMethod,
    handleInputChange,
    emailHasError: Boolean(errors.email),
    phoneHasError: Boolean(errors.phone),
  };
};
